import React, {useMemo} from "react";
import Button from "../../../../../components/button/button.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {FaTrash} from "react-icons/fa";
import {capitalizeFirstLetter, isIntegrantRequired} from "../utils/garrison-utils.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import {FaCircleExclamation, FaTriangleExclamation} from "react-icons/fa6";

const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const maintenanceLabel = {
    expired: {text: "Manutenção vencida", className: "text-red-600"},
    toExpire: {text: "Manutenção a vencer", className: "text-[#fbae1d]"},
};

const GarrisonCompositionRow = ({
    row, compositionOptions, contingentOptions, usedCompositionValues, usedContingentValues,
    onSelectComposition, onChangeKm, onChangeIntegrant, onRemove, canRemove,
}) => {
    const getCnhWarningIcon = (integrant) => {
        if (!integrant.conductor) {
            return null;
        }

        switch (integrant.cnhWarning) {
            case "danger":
                return <FaTriangleExclamation className="text-red-500" />;
            case "warning":
                return <FaCircleExclamation className="text-amber-500" />;
            default:
                return null;
        }
    };

    const selectedOption = compositionOptions.find(option => String(option.value) === String(row.compositionId)) ?? null;

    const availableCompositionOptions = useMemo(() => compositionOptions.map(option => ({
        ...option,
        isDisabled: option.label !== "Bicicleta"
            && String(option.value) !== String(row.compositionId)
            && usedCompositionValues.includes(String(option.value)),
    })), [compositionOptions, usedCompositionValues, row.compositionId]);

    const maintenance = maintenanceLabel[row.maintenanceExpired];

    return (
        <div className="border border-[#ccc] rounded-md p-3 space-y-4">
            <SentryRow className="items-start">
                <div className="col-span-4">
                    {row.disabled ? (
                        <InputForm
                            id={`composition-${row.tempId}`}
                            label="Composição *"
                            value={row.displayName ?? ""}
                            disabled
                            span={
                                maintenance &&
                                    <span className={`absolute -top-4 left-2 text-xs ${maintenance.className}`}>
                                        {maintenance.text}
                                    </span>
                            }
                        />
                    ) : (
                        <SelectComponent
                            classNameWrapper="!bg-[#fdfdee]"
                            label="Composição *"
                            required
                            options={availableCompositionOptions}
                            isClearable={false}
                            value={selectedOption}
                            labelClass={`${maintenance?.className ?? ""} ${REQUIRED_LABEl_BG}`}
                            onChange={(option) => onSelectComposition(row.tempId, option)}
                            helperText={maintenance?.text}
                            status={row.maintenanceExpired === "expired" ? "danger" : (row.maintenanceExpired === "toExpire" ? "warning" : undefined)}
                        />
                    )}
                </div>

                {row.hasVehicle &&
                    <>
                        <div className="col-span-3">
                            <InputForm
                                key={`initial-mileage-${row.tempId}-${row.compositionId}`}
                                id={`initial-mileage-${row.tempId}`}
                                label={`Km inicial${row.kmControl ? " *" : ""}`}
                                type="number"
                                value={row.initialMileage ?? ""}
                                disabled={!row.kmControl}
                                required={row.kmControl}
                                onChange={({target: {value}}) => onChangeKm(row.tempId, "initialMileage", value)}
                                integerOnly
                            />
                        </div>
                        <div className="col-span-3">
                            <InputForm
                                key={`final-mileage-${row.tempId}-${row.compositionId}`}
                                id={`final-mileage-${row.tempId}`}
                                label="Km final"
                                type="number"
                                value={row.finalMileage ?? ""}
                                disabled={!row.kmControl}
                                onChange={({target: {value}}) => onChangeKm(row.tempId, "finalMileage", value)}
                                integerOnly
                            />
                        </div>
                    </>
                }

                {canRemove &&
                    <div className="col-span-1">
                        <Button target="danger" title="Remover composição" onClick={() => onRemove(row.tempId)}><FaTrash/></Button>
                    </div>
                }
            </SentryRow>

            {row.compositionId && row.integrants.length > 0 &&
                <SentryRow>
                    {row.integrants.map((integrant, index) => {
                        const selectedIntegrant = contingentOptions.find(option => String(option.value) === String(integrant.contingentId)) ?? null;
                        const options = contingentOptions.map(option => ({
                            ...option,
                            isDisabled: String(option.value) !== String(integrant.contingentId)
                                && usedContingentValues.includes(String(option.value)),
                        }));
                        const label = capitalizeFirstLetter(integrant.position ?? "")
                            .replace(/\s*\*\s*$/, "");
                        const required = isIntegrantRequired(integrant);

                        return (
                            <div className="col-span-3" key={`${row.tempId}-${row.compositionId}-${index}`}>
                                <SelectComponent
                                    classNameWrapper={isIntegrantRequired(integrant) ? "!bg-[#fdfdee]" : "bg-white"}
                                    label={label}
                                    labelClass={isIntegrantRequired(integrant) ? REQUIRED_LABEl_BG : ""}
                                    required={required}
                                    options={options}
                                    isDisabled={row.disabled}
                                    isClearable={!row.disabled}
                                    value={selectedIntegrant}
                                    onChange={(option) => onChangeIntegrant(row.tempId, index, option)}
                                    status={integrant.conductor && integrant.cnhWarning ? integrant.cnhWarning : undefined}
                                    helperText={integrant.conductor ? integrant.cnhWarningText : ""}
                                    customIcon={getCnhWarningIcon(integrant)}
                                />
                            </div>
                        );
                    })}
                </SentryRow>
            }
        </div>
    );
};

const GarrisonCompositionList = ({
    rows, withVehicleOptions, withoutVehicleOptions, contingentOptions,
    onSelectComposition, onChangeKm, onChangeIntegrant, onRemove, canRemove,
}) => {
    const usedCompositionValues = rows
        .map(row => (row.compositionId != null && row.compositionId !== "" ? String(row.compositionId) : null))
        .filter(Boolean);

    const usedContingentValues = rows
        .flatMap(row => row.integrants ?? [])
        .map(integrant => (integrant.contingentId != null && integrant.contingentId !== "" ? String(integrant.contingentId) : null))
        .filter(Boolean);

    return (
        <div className={"space-y-4"}>
            {rows.map(row => (
                <GarrisonCompositionRow
                    key={row.tempId}
                    row={row}
                    compositionOptions={row.hasVehicle ? withVehicleOptions : withoutVehicleOptions}
                    contingentOptions={contingentOptions}
                    usedCompositionValues={usedCompositionValues.filter(value => value !== String(row.compositionId))}
                    usedContingentValues={usedContingentValues}
                    onSelectComposition={onSelectComposition}
                    onChangeKm={onChangeKm}
                    onChangeIntegrant={onChangeIntegrant}
                    onRemove={onRemove}
                    canRemove={canRemove}
                />
            ))}
        </div>
    );
};

export default GarrisonCompositionList;